'use client';
import { TypeSubFlowV3Translations } from '@/utils/translations/TypeSubFlowV3Translations';
import {
  SubFlowV3ContextApi,
  SubFlowV3ContextData,
} from '../../context/SubFlowV3Context';
import { PaymentOption } from '@/hooks/usePaymentOptions';
import { FC, useEffect, useState } from 'react';
import { Button, HeaderText } from '@/components';
import { BUTTON_SIZES } from '@/components/ui/Buttons/utils';
import RadioPaymentOption from './RadioPaymentOption';
import { HeaderSize } from '@/components/ui/HeaderText';
import BookBundleAddOn from './BookBundleAddOn';
import loader from '@/components/subflow-v3/assets/loader_transparent.gif';
import Image from 'next/image';
import { SUBSCRIPTION_LANGUAGE_OPTIONS } from '@/lib/api/subscriptionManager/types';
import interpolate from '@/utils/translations/interpolate';
import { getBundleDiscountValue } from '../utils';
import classNames from 'classnames';
import useFeature from '@/hooks/useFeature';
import { PaymentOptionExtend } from '@/components/common/types';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { KIT_ANCHORS } from '@/components/subflow-v3/components/utils';
import { getDefaultPaymentOption } from '@/components/subflow-v3/components/RadioPaymentSelector/utils';
export interface RadioPaymentSelectorProps {
  data: SubFlowV3ContextData;
  api: SubFlowV3ContextApi;
  onSelect?: (option: PaymentOption) => void;
  t: TypeSubFlowV3Translations;
  handleSubmit: (paymentOption: PaymentOption | null) => void;
  loadingCheckout: boolean;
  drawerStyles?: boolean;
}

const KIT_ANCHOR_VALUES = {
  [KIT_ANCHORS.ANALYST]: 1,
  [KIT_ANCHORS.PROBLEM_SOLVER]: 2,
};

const RadioPaymentSelector: FC<RadioPaymentSelectorProps> = ({
  data,
  api,
  t,
  onSelect,
  handleSubmit,
  loadingCheckout,
  drawerStyles,
}) => {
  const isBfcm2024 = useFeature('money_enable-bfcm-2024');
  const { 'growth_pdp-flexibility': growthPdpFlexibilityFlag } = useFlags();
  const { 'sub-squad-enable-big-puf': subSquadEnableBigPuf } = useFlags();

  const prePayIndex = 1;
  const [selectedOption, setSelectedOption] = useState<
    PaymentOption | PaymentOptionExtend | null
  >(null);
  const [options, setOptions] = useState<PaymentOption[] | null>(null);
  const isEnglishSelected =
    data.language === SUBSCRIPTION_LANGUAGE_OPTIONS.EN ||
    data.language === 'en-GB' ||
    // If this is undefined, backend treats it as english by default
    data.language === undefined;

  // bookBundlesLength is 2 for the problem solver and 1 for the analyst because the 4YO kits don’t include book bundles, and these two kits are the last ones that still have book bundles.
  const bookBundlesLength =
    KIT_ANCHOR_VALUES[data.startingKit?.anchor ?? ''] ?? 3;

  const hasBookAddon =
    data.addOns?.subscription_add_ons &&
    data.addOns?.subscription_add_ons.length >= bookBundlesLength;
  const isAUStore = process.env.NEXT_PUBLIC_REGION === 'AU';
  //  Book bundles are not available in AU store for now.
  const shouldRenderBookBundle =
    !isAUStore && hasBookAddon && isEnglishSelected;

  useEffect(() => {
    if (data.paymentOptions) {
      setSelectedOption(getDefaultPaymentOption(data));

      // we only want ppk and puf, the first two payment options
      if (!subSquadEnableBigPuf) {
        const relevantPaymentOptions =
          data.paymentOptions.payment_options.slice(0, 2);

        setOptions(relevantPaymentOptions.reverse());
      } else {
        setOptions(data.paymentOptions.payment_options);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.paymentOptions]);
  useEffect(() => {
    const addonOption = options?.find((option) => {
      if ('isAddon' in option && option.isAddon) {
        return option;
      }
    });

    //   If addon was previously added, last payment card should be pre-selected
    if (addonOption && data.subscriptionAddOns.length > 0) {
      setSelectedOption(addonOption);
    }
  }, [options, hasBookAddon, data.subscriptionAddOns.length]);

  const handleSelect = (option: PaymentOption) => {
    if (option.uuid !== selectedOption?.uuid) {
      setSelectedOption(option);
      onSelect?.(option);

      const isAddonAvailable = 'isAddon' in option && option.isAddon;

      const isBigPufBundleAvailable =
        data.subscriptionAddOns.length > 0 && subSquadEnableBigPuf;

      if (isAddonAvailable || isBigPufBundleAvailable) {
        const sliceSize = subSquadEnableBigPuf ? option.size : 3;

        const addOns = data.addOns?.subscription_add_ons.slice(0, sliceSize);

        api.toggleSubscriptionAddOns(addOns, subSquadEnableBigPuf);
      }
    }
  };

  if (!data.paymentOptions || !options || data.isBundleDiscountPreviewLoading)
    return (
      <div className="mt-10 flex justify-center">
        <Image src={loader} alt="loader" />
      </div>
    );

  const paymentOptionDescription = [
    t.paymentOptionDescription1,
    t.paymentOptionDescription2,
  ];

  const handleFormSubmit = (selectedOption: PaymentOption | null) => {
    if (
      selectedOption &&
      'isAddon' in selectedOption &&
      selectedOption.isAddon
    ) {
      // Send 3-Kit Prepay as paymentOption if a Book Bundle is added
      handleSubmit(options[prePayIndex]);
      return;
    }
    handleSubmit(selectedOption);
  };

  // Calculate bundle discount based on selected options for BFCM 2024
  const bundleDiscountValue = getBundleDiscountValue(
    selectedOption,
    data.bundleDiscountPreview?.bundle_discount_preview?.bundles,
    data.subscriptionAddOns.length > 0,
  );

  return (
    <div
      className={classNames({
        'lg:mb-12': !drawerStyles,
        'flex flex-col align-start w-full': drawerStyles,
      })}
    >
      {!drawerStyles && (
        <HeaderText
          size={HeaderSize.H1}
          align="center"
          classes={classNames(
            'leading-tight lg:leading-8 sm:w-[295px] md:w-full',
            isBfcm2024 ? 'mb-2 md:mb-3' : 'mb-4 md:mb-10',
          )}
          text={t.paymentOptionsHeader}
        />
      )}
      {isBfcm2024 && (
        <p className="text-base text-center text-red-v2 mb-8 mx-auto max-w-64 md:max-w-full">
          {t.bfcm2024PaymentOptionsSubheader}
        </p>
      )}
      <div
        className={classNames('block lg:flex items-stretch lg:pb-0', {
          'lg:space-x-9 lg:mx-40': options.length < 3 && !drawerStyles,
          'lg:flex-row pb-4': !drawerStyles,
          'flex-col w-full pb-1': drawerStyles,
        })}
      >
        {options.map((option) => (
          <RadioPaymentOption
            className={classNames('!p-0', {
              '!py-4 lg:!p-4': !subSquadEnableBigPuf,
              '!py-3 lg:!p-3': subSquadEnableBigPuf,
            })}
            key={option.uuid}
            option={option}
            selectedOption={selectedOption}
            t={t}
            onSelect={handleSelect}
            allOptions={options}
            drawerStyles={drawerStyles}
          />
        ))}
      </div>

      <div
        className={classNames('flex flex-col', {
          'align-start w-full': drawerStyles,
        })}
      >
        <div
          className={classNames('flex pl-4 sm:justify-start', {
            'md:justify-center lg:mt-4': !drawerStyles,
            'lg:mt-2': drawerStyles,
          })}
        >
          <ul
            className={classNames('list-outside flex-col pl-1 mx-3', {
              'align-start': drawerStyles,
              'md:items-center': !drawerStyles,
            })}
          >
            {paymentOptionDescription.map((item, index) => (
              <li
                key={index}
                className={classNames(
                  'list-image-purpleCircle pl-1 max-w-[880px]',
                  {
                    'lg:text-center text-[16px] lg:text-[18px] my-2':
                      !drawerStyles,
                    'lg:text-[16px] text-sm': drawerStyles,
                  },
                )}
              >
                {item}
              </li>
            ))}
          </ul>
        </div>
        {shouldRenderBookBundle && (
          <div
            className={classNames(
              'flex flex-col justify-center items-center py-3 lg:pt-8',
              {
                'lg:px-4 pt-8': drawerStyles,
              },
            )}
          >
            {!drawerStyles && (
              <div className="block lg:hidden w-12 border-grey-midtone border-t pb-10" />
            )}
            <BookBundleAddOn
              t={t}
              data={data}
              api={api}
              selectedOptionSize={selectedOption?.size}
              drawerStyles={drawerStyles}
            />
          </div>
        )}
        {isBfcm2024 && bundleDiscountValue > 0 && (
          <div className="text-center text-red-v2 mt-4 md:mt-6">
            <p className="text-lg">
              <strong>
                {interpolate(t.bfcm2024PaymentOptionsDiscount, {
                  discount: bundleDiscountValue,
                })}
              </strong>
            </p>
            <p className="text-sm">{t.bfcm2024PaymentOptionsSavings}</p>
          </div>
        )}
      </div>

      <div
        className={classNames({
          'mt-7 mx-auto lg:w-[400px]': !drawerStyles,
          'w-full fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-grey-mid-light':
            drawerStyles,
        })}
      >
        <Button
          classes={`btn-responsive h-14`}
          ctaText={
            growthPdpFlexibilityFlag || drawerStyles
              ? t.completeOrderButton
              : t.childInfoButton
          }
          fullWidth
          size={BUTTON_SIZES.lg}
          type="submit"
          onClick={() => handleFormSubmit(selectedOption)}
          disabled={loadingCheckout}
          includeSpinner={loadingCheckout}
        />
      </div>

      <div
        className={classNames('flex justify-center', {
          'pt-7': !drawerStyles,
          'pt-3': drawerStyles,
        })}
      >
        <p
          className={classNames('text-charcoal mb-2 text-[14px] leading-6', {
            'max-w-[424px] text-center md:text-[16px]': !drawerStyles,
            'w-full lg:px-4 text-left': drawerStyles,
          })}
        >
          {t.pufCaptionText}
        </p>
      </div>
    </div>
  );
};

export default RadioPaymentSelector;
